// Контракт API пульта (задача D2): «Почему Анна не ответила?» и повтор упавшего хода.
//
// `GET /api/v1/why/:ticketNumberOrId` (любой вошедший) → `WhyResponse`: тикет с подписями (номер, клиент, канал, ссылка
// на чат в Омнидеске), ворота сейчас, что предложить человеку (`action`) и последний ход. Телефона клиента в ответе нет
// ни для какой роли. Действие: ворота держит причина из `LAUNCH_GATE_REASONS` — `open_launch`; ворота открыты, а
// последний ход упал, — `replay_turn` (Ruling 83: при любых закрытых воротах повтор не предлагается); иначе `none`.
// `POST /api/v1/turns/:id/replay` (admin) → 202 `ReplayResponse`; повторить можно только упавший ход (`failed`), иначе
// 409 `not_failed` (Ruling 86); ход идёт — 409 `turn_running`.
//
// Перечисления — копии серверных:
// - причины ворот A8 (`GATE_REASONS` в `src/pipeline/types.ts`): совпадение типов держит
//   `tests/console/gate-reasons.type-test.ts` (`npm run typecheck`), порядок — `tests/console/contract.test.ts`;
// - коды сбоя модели C1-2 (`MODEL_FAILURES`, тип `ModelFailure` из `src/ai/errors.ts`). Тест равенства типов
//   `tests/console/model-failures.type-test.ts` добавляет та из задач D2 и C1-2, что сливается в основную ветку второй
//   (Ruling 75); до него новый код C1-2 получит подпись `other`.
import { z } from 'zod';
import { MomentSchema } from './common.js';

/**
 * Причины, по которым ворота не пускают Анну к ходу, в порядке проверки (A8; режим диалога в бою — L1). Четыре последние —
 * не ворота: `technical_only` ставит ход до разбора (клиент только нажимал кнопки меню TextBack или писал «/start»),
 * `automation_replied` — тоже до разбора (после последнего сообщения клиента ответила автоматика TextBack, Q1),
 * `awaiting_human` — ход после разбора (L2), `after_reject` — тоже после разбора (план A21: после отказа клиент написал то,
 * на что ответ не нужен, или в чат уже пишет сотрудник), `mail_triaged` — письмо отсортировано до модели ответа (план MAIL,
 * A3: служебное, автоответ, переписка сотрудника или не запрос клиента).
 */
export const GATE_REASONS = [
  'lease_busy',
  'mode_off',
  'released',
  'channel_off',
  'not_in_share',
  'not_leading',
  'ticket_closed',
  'stale_inbound',
  'human_active',
  'stop_contact',
  'budget_exhausted',
  'technical_only',
  'automation_replied',
  'awaiting_human',
  'after_reject',
  'mail_triaged',
] as const;
export const GateReasonSchema = z.enum(GATE_REASONS);
export type GateReason = z.infer<typeof GateReasonSchema>;

/** Причины, которые чинят в «Запуске»: общий режим, режим канала, доля новых чатов, лимиты бюджета. */
export const LAUNCH_GATE_REASONS = ['mode_off', 'channel_off', 'not_in_share', 'budget_exhausted'] as const satisfies readonly GateReason[];

/** Что предложить человеку: открыть «Запуск», повторить упавший ход или ничего. */
export const WHY_ACTION_KINDS = ['open_launch', 'replay_turn', 'none'] as const;
export const WhyActionKindSchema = z.enum(WHY_ACTION_KINDS);
export type WhyActionKind = z.infer<typeof WhyActionKindSchema>;

/** Подпись кнопки действия; у `none` подписи нет. */
export const WHY_ACTION_RU: Readonly<Record<Exclude<WhyActionKind, 'none'>, string>> = {
  open_launch: 'Открыть «Запуск»',
  replay_turn: 'Повторить ход',
};

/** Коды сбоя вызова модели (C1-2, `ModelFailure`): пишутся в `turns.error`. */
export const MODEL_FAILURES = [
  'quota_exhausted',
  'rate_limited',
  'overloaded',
  'auth',
  'misalignment',
  'bad_request',
  'no_output',
  'timeout',
  'call_limit',
  'not_configured',
  'other',
] as const;
export type ModelFailure = (typeof MODEL_FAILURES)[number];

/**
 * Сбой модели словами — в пульт коды вроде `overloaded` не попадают. `other` — и для всего, чего в списке нет: в
 * `turns.error` может лежать текст неожиданного исключения (C1-14), его пульт не показывает.
 */
export const MODEL_FAILURE_RU: Readonly<Record<ModelFailure, string>> = {
  quota_exhausted: 'кончился лимит проекта OpenAI',
  rate_limited: 'OpenAI ограничил частоту запросов',
  overloaded: 'OpenAI перегружен',
  auth: 'ключ OpenAI не принят',
  misalignment: 'OpenAI отказал по своим правилам',
  bad_request: 'ошибка в запросе к модели',
  no_output: 'модель не вернула ответ',
  timeout: 'ход не уложился в 240 с',
  call_limit: 'исчерпан лимит 25 вызовов модели на диалог',
  not_configured: 'нет ключа OpenAI',
  other: 'непредвиденная ошибка — подробности в журнале ai-qual-worker',
};

/**
 * Тикет: номер из заголовка чата в Омнидеске, клиент коротко («Иван П.», без имени — «Клиент #48213»; номер телефона
 * вместо имени — маской), канал словами («Авито «Кабинет 3»») и ссылка на чат в Омнидеске.
 */
export const WhyTicketSchema = z.object({
  id: z.string().min(1),
  number: z.number().int().positive(),
  customerLabel: z.string().min(1),
  channelLabel: z.string().min(1),
  omniUrl: z.url(),
});
export type WhyTicket = z.infer<typeof WhyTicketSchema>;

/**
 * Ворота прямо сейчас — так же, как их считает ход: пропускают ли, первая сработавшая причина (`null` — пропускают),
 * что случилось и что сделать (`fixRu: null` — сказать нечего).
 */
export const WhyGateSchema = z.object({
  allowed: z.boolean(),
  reason: GateReasonSchema.nullable(),
  explanationRu: z.string().min(1),
  fixRu: z.string().nullable(),
});
export type WhyGate = z.infer<typeof WhyGateSchema>;

/** Действие для человека: вид и подпись кнопки (`null` у `none`). */
export const WhyActionSchema = z.object({ kind: WhyActionKindSchema, labelRu: z.string().nullable() });
export type WhyAction = z.infer<typeof WhyActionSchema>;

/**
 * Последний ход диалога: номер среди ходов без заменённых (`superseded` получает номер хода, который его заменит; по
 * нему «Разбор ›» открывает `/dialogs/{dialogId}?turn={no}`), момент записи, состояние и подпись для строки
 * «Последний ход: {момент} — {подпись}».
 */
export const WhyLastTurnSchema = z.object({
  turnId: z.string().min(1),
  no: z.number().int().positive(),
  at: MomentSchema,
  status: z.string().min(1),
  labelRu: z.string().min(1),
});
export type WhyLastTurn = z.infer<typeof WhyLastTurnSchema>;

/** Ответ «Почему Анна не ответила?»; `dialogId` и `lastTurn` — `null`, пока Анна чат не видела. */
export const WhyResponseSchema = z.object({
  ticket: WhyTicketSchema,
  dialogId: z.string().min(1).nullable(),
  gate: WhyGateSchema,
  action: WhyActionSchema,
  lastTurn: WhyLastTurnSchema.nullable(),
  at: MomentSchema,
});
export type WhyResponse = z.infer<typeof WhyResponseSchema>;

/** Ответ повтора хода (202): когда задача хода поставлена в очередь — пульт пишет «Ход поставлен в очередь · 14:32 МСК». */
export const ReplayResponseSchema = z.object({ queuedAt: MomentSchema });
export type ReplayResponse = z.infer<typeof ReplayResponseSchema>;

/** Коды ошибок «Почему» и повтора хода и их HTTP-статусы (копия `API_ERROR_STATUS`, `src/console/errors.ts`). */
export const WHY_ERROR_STATUS = {
  /** Не номер и не id тикета. */
  bad_request: 400,
  /** Тикета нет; хода нет; у диалога нет входящих — текст свой в каждом случае. */
  not_found: 404,
  /** Тикет не отдела Квалификация. */
  not_qualifier: 404,
  /** Ход по этому диалогу идёт прямо сейчас. */
  turn_running: 409,
  /** Повторяют ход, который не упал: в бою это был бы второй ответ клиенту на то же сообщение. */
  not_failed: 409,
  /** База Омнидеска не ответила. */
  omni_read_failed: 502,
} as const;
export type WhyErrorCode = keyof typeof WHY_ERROR_STATUS;

/** Постоянные тексты этих ошибок (копия `API_ERRORS_RU`). */
export const WHY_ERRORS_RU: Readonly<Record<'turn_running' | 'not_failed' | 'omni_read_failed', string>> = {
  turn_running: 'Ход уже идёт — дождитесь его конца',
  not_failed: 'Повторить можно только упавший ход',
  omni_read_failed:
    'База Омнидеска не ответила, поэтому данные из неё сейчас не прочитать. Посмотрите «Готовность» → «Чтение Омнидеска» и попробуйте через минуту.',
};
